import React, { useEffect, useRef, useState } from 'react';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { ColumnsIcon } from '../ui/Icons';
import { COLUMN_CONFIG, ColumnKey } from './helpers';

interface ColumnVisibilityDropdownProps {
  visibleColumns: ColumnKey[];
  onToggleColumn: (key: ColumnKey) => void;
  onShowAll: () => void;
}

export default function ColumnVisibilityDropdown({
  visibleColumns,
  onToggleColumn,
  onShowAll
}: ColumnVisibilityDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allVisible = visibleColumns.length === COLUMN_CONFIG.length;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        type={ButtonType.transparent}
        size={ButtonSize.small}
        classNameButton="p-[5px]"
        aria-label="Toggle column visibility"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <ColumnsIcon size="18px" />
      </Button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-lg z-50 py-2"
          role="menu"
          aria-label="Column visibility options"
        >
          <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-gilroy-bold text-neutral-600">Columns</span>
            {!allVisible && (
              <button
                type="button"
                onClick={onShowAll}
                className="text-xs text-primary-600 hover:text-primary-700 font-gilroy-medium"
              >
                Show All
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto py-1">
            {COLUMN_CONFIG.map(({ key, label }) => {
              const isVisible = visibleColumns.includes(key);
              const isLastVisible = isVisible && visibleColumns.length === 1;

              return (
                <label
                  key={key}
                  className={`flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors hover:bg-gray-50 ${
                    isLastVisible ? 'opacity-60' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={() => onToggleColumn(key)}
                    disabled={isLastVisible}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-offset-0"
                  />
                  <span className="text-sm text-neutral-600 font-gilroy-medium">{label}</span>
                </label>
              );
            })}
          </div>

          <div className="px-4 py-2 border-t border-gray-100">
            <p className="text-xs text-neutral-400">
              {visibleColumns.length} of {COLUMN_CONFIG.length} columns visible
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
